import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, ArrowRight } from 'lucide-react';
import BrandLogo from './BrandLogo';
import { productsAPI } from '../services/api';
import { productPath } from '../services/productPath';
import { useT } from '../services/i18n';
import { prodName } from '../services/catalogI18n';

// Home strip « Nouveautés de la semaine » : latest parts added over 7 days + link to /nouveautes (hidden when nothing new)
const HomeNewArrivals = () => {
  const { t, lang } = useT();
  const ref = useRef(null);
  const [data, setData] = useState(null);
  useEffect(() => { productsAPI.getNew(7, null, 12).then(setData).catch(() => setData({ total: 0, products: [] })); }, []);
  if (!data || !data.total) return null;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 300, behavior: 'smooth' });
  return (
    <section className="py-6 sm:py-10 bg-gradient-to-b from-orange-50/70 to-white" data-testid="home-new-arrivals">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2"><Sparkles className="text-orange-500" size={22} /> {t('new.homeTitle')}</h2>
            <p className="text-sm text-gray-500 mt-1" data-testid="home-new-arrivals-count">{t('new.homeCount', { n: data.total })}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex gap-2">
              <button type="button" onClick={() => scroll(-1)} aria-label="←" className="p-2 rounded-full border border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-colors"><ChevronLeft size={18} /></button>
              <button type="button" onClick={() => scroll(1)} aria-label="→" className="p-2 rounded-full border border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-colors"><ChevronRight size={18} /></button>
            </div>
            <Link to="/nouveautes" data-testid="home-new-arrivals-link" className="inline-flex items-center gap-1.5 bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">{t('new.homeAll')} <ArrowRight size={15} /></Link>
          </div>
        </div>
        <div ref={ref} className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 scroll-smooth snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="home-new-arrivals-strip">
          {data.products.map((p) => (
            <Link key={p.id} to={productPath(p)} data-testid={`home-new-arrival-${p.id}`} className="group relative flex-shrink-0 w-44 sm:w-52 snap-start bg-white border border-gray-200 rounded-xl p-3 hover:shadow-lg hover:border-orange-200 transition-[box-shadow,border-color]">
              <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full"><Sparkles size={10} /> {t('new.badge')}</span>
              <div className="h-28 sm:h-32 flex items-center justify-center mb-2">
                {p.image ? <img src={p.image} alt={prodName(p, lang)} loading="lazy" className="max-h-full max-w-full object-contain transition-transform duration-200 group-hover:scale-105" /> : <div className="w-full h-full rounded-lg bg-gray-50" />}
              </div>
              <div className="text-xs text-gray-500"><BrandLogo brand={p.brand} size="xs" testId={`home-new-arrival-brand-${p.id}`} /></div>
              <p className="text-sm font-medium text-gray-900 line-clamp-2 min-h-[2.5rem] group-hover:text-orange-600 transition-colors">{prodName(p, lang)}</p>
              <p className="text-base font-bold text-gray-900 mt-1">{Number(p.price || 0).toFixed(2)} €</p>
            </Link>
          ))}
          <Link to="/nouveautes" data-testid="home-new-arrivals-more" className="flex-shrink-0 w-36 snap-start rounded-xl border-2 border-dashed border-orange-200 flex flex-col items-center justify-center text-center text-sm font-semibold text-orange-600 hover:bg-orange-50 transition-colors p-3">
            <ArrowRight size={22} className="mb-1" /> {t('new.homeAll')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeNewArrivals;
